import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { firstValueFrom, Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { AppNotification } from 'src/app/models/notification';
import { ApiService } from 'src/app/services/api.service';
import { NotificationService } from 'src/app/services/notification.service';
import { getDateFormat } from 'src/app/utils/date.util';
import { getNotificationText } from 'src/app/utils/notification.util';

const LATEST_LIMIT = 5;

@Component({
  selector: 'app-notification-bell',
  imports: [CommonModule, RouterModule, MaterialModule, TablerIconsModule, TranslateModule],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  // El header horizontal abre el menú hacia la izquierda
  @Input() xPosition: 'before' | 'after' = 'after';

  unreadCount: number | null = null;
  latest: AppNotification[] = [];
  isLoading = false;
  hasError = false;
  translationsReady = false;
  getDateFormat = getDateFormat;

  private userId = '';
  private subscriptions = new Subscription();

  constructor(
    private apiServ: ApiService,
    private notificationServ: NotificationService,
    private translate: TranslateService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Los textos de las notificaciones se resuelven con translate.instant: se espera a que carguen las traducciones
    firstValueFrom(this.translate.get('notifications.generic.title')).then(() => (this.translationsReady = true));

    this.subscriptions.add(this.notificationServ.unreadCount$.subscribe((count) => (this.unreadCount = count)));
    // Contador al iniciar / iniciar sesión. user$ vuelve a emitir con cada employee_update: solo se
    // consulta cuando cambia el usuario.
    this.subscriptions.add(this.apiServ.user$.subscribe((user) => {
      const id = user?._id || '';
      if (id === this.userId) return;
      this.userId = id;
      this.latest = [];
      if (id) {
        this.notificationServ.refreshUnreadCount();
      } else {
        this.notificationServ.resetUnreadCount();
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Sin polling: el contador también se actualiza cuando la ventana recupera el foco
  @HostListener('window:focus')
  onWindowFocus(): void {
    if (this.userId) this.notificationServ.refreshUnreadCount();
  }

  get badgeValue(): string | number {
    return this.unreadCount !== null && this.unreadCount > 99 ? '99+' : (this.unreadCount ?? '');
  }

  async onMenuOpened(): Promise<void> {
    if (!this.userId) return;
    this.notificationServ.refreshUnreadCount();
    await this.loadLatest();
  }

  async loadLatest(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    try {
      const response = await this.notificationServ.getNotifications(1, LATEST_LIMIT);
      this.latest = response.data;
    } catch (error) {
      console.error('Error al cargar las notificaciones');
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  text(notification: AppNotification) {
    return getNotificationText(this.translate, notification);
  }

  // Ver la notificación NO la marca como leída: el clic abre /notification-redirect/:id, que la marca
  // como leída, resuelve el destino y navega (misma lógica que la pantalla /notifications).
  open(notification: AppNotification): void {
    this.router.navigate(['/notification-redirect', notification._id]);
  }
}
